<template>
  <div class="header-journeys-dropdown w-full shadow-lg">
    <div class="header-journeys-dropdown__container">
      <!-- Left: Image -->
      <div class="header-journeys-dropdown__image-section">
        <img alt="Journey" class="header-journeys-dropdown__image" loading="lazy" :src="destinationImage" />
      </div>
      <!-- Right: Content -->
      <div class="header-journeys-dropdown__content">
        <!-- Short-Stay Itineraries -->
        <div class="header-journeys-dropdown__section">
          <h3 class="header-journeys-dropdown__section-title">Short-Stay Itineraries</h3>
          <div class="header-journeys-dropdown__area-list">
            <a v-for="area in shortStayAreas" :key="area.id" class="header-journeys-dropdown__area-item" :href="getShortStayLink(area.id)" @click="onClickShortStayArea(area.label)">
              <span>{{ area.label }}</span>
              <span class="header-journeys-dropdown__arrow" />
            </a>
          </div>
        </div>
        <!-- Multi-Day Itineraries -->
        <div class="header-journeys-dropdown__section">
          <h3 class="header-journeys-dropdown__section-title">Multi-Day Itineraries</h3>
          <div class="header-journeys-dropdown__multiday-list">
            <NuxtLink
              v-for="itinerary in multiDayItineraries"
              :key="itinerary.id"
              class="header-journeys-dropdown__multiday-item"
              :to="itinerary.link"
              @click="onClickMultiDayItinerary(itinerary.titleEn)"
            >
              <span>{{ itinerary.titleEn }}</span>
              <span class="header-journeys-dropdown__arrow" />
            </NuxtLink>
          </div>
        </div>
        <!-- View more -->
        <a class="header-journeys-dropdown__view-more" :href="SHORT_STAY_BASE_URL" @click="onClickViewMore">
          <span>View more</span>
          <span class="header-journeys-dropdown__view-more-arrow" />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import destinationImage from '@/wabunka-src/common/assets/img/top/destinations_pc.png';
import { generateDetailPageLink, type MinimumFeaturedListViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import { sendGAWabunkaClickHeaderNavEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickHeaderNav';
import { SHORT_STAY_AREAS, NAVIGATION_MULTI_DAY_LIMIT, SHORT_STAY_BASE_URL } from '@/wabunka-src/constants/navigation';
import { ITINERARY_TAG_DAYS_THRESHOLD } from '@/wabunka-src/features/Itinerary/constants/itineraryTagLabels';

interface Props {
  itineraries: MinimumFeaturedListViewModel;
}

const props = defineProps<Props>();

const shortStayAreas = SHORT_STAY_AREAS;

/** Multi-Day旅程（日数 > 3、または日数未設定は除外） */
const multiDayItineraries = computed(() =>
  props.itineraries
    .filter((itinerary) => {
      const days = itinerary.itineraryTagSettings?.days;
      return days !== undefined && days > ITINERARY_TAG_DAYS_THRESHOLD;
    })
    .slice(0, NAVIGATION_MULTI_DAY_LIMIT)
    .map((itinerary) => ({
      ...itinerary,
      link: generateDetailPageLink(itinerary),
    })),
);

/** Short-Stayエリアのリンクを生成 */
const getShortStayLink = (areaId: string) => {
  return `${SHORT_STAY_BASE_URL}#${areaId}`;
};

const onClickShortStayArea = (label: string) => {
  sendGAWabunkaClickHeaderNavEvent('journeys', label, 'header');
};

const onClickMultiDayItinerary = (titleEn: string) => {
  sendGAWabunkaClickHeaderNavEvent('journeys', titleEn, 'header');
};

const onClickViewMore = () => {
  sendGAWabunkaClickHeaderNavEvent('journeys', 'view_more', 'header');
};
</script>

<style lang="scss" scoped>
.header-journeys-dropdown {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  &__container {
    display: flex;
    gap: 32px;
    max-width: 1400px;
  }
  &__image-section {
    flex-shrink: 0;
    width: 14%;
    max-width: 200px;
    aspect-ratio: 269 / 336;
    transition: transform 0.2s ease;
  }
  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 32px 48px 32px 0;
  }
  &__section-title {
    font-family: 'Spectral', serif;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: 0.5px;
    text-transform: capitalize;
    color: #b8860b;
    margin-bottom: 8px;
  }
  &__area-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
  }
  &__area-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: 'Spectral', serif;
    font-size: 17px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    color: #171717;
    text-decoration: none;
    transition: color 0.3s ease;
    &:hover {
      color: #b8860b;
      .header-journeys-dropdown__arrow {
        border-color: #b8860b;
      }
    }
  }
  &__arrow {
    width: 6px;
    height: 6px;
    border-top: solid 1px #333;
    border-right: solid 1px #333;
    transform: rotate(45deg);
    transition: border-color 0.3s ease;
  }
  &__multiday-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px 32px;
  }
  &__multiday-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: 'Spectral', serif;
    font-size: 17px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    color: #171717;
    text-decoration: none;
    transition: color 0.3s ease;
    &:hover {
      color: #b8860b;
      .header-journeys-dropdown__arrow {
        border-color: #b8860b;
      }
    }
  }
  &__view-more {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: auto;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    vertical-align: middle;
    color: #171717;
    transition: color 0.3s ease;
    &:hover {
      color: #b8860b;
      .header-journeys-dropdown__view-more-arrow {
        border-color: #b8860b;
      }
    }
  }
  &__view-more-arrow {
    width: 6px;
    height: 6px;
    border-top: solid 1px #333;
    border-right: solid 1px #333;
    transform: rotate(45deg);
    transition: border-color 0.3s ease;
  }
}

:global(.dropdown-enter-active) .header-journeys-dropdown__image-section {
  opacity: 0;
  animation: journeyImageFadeIn 0.1s ease 0.15s forwards;
}

:global(.dropdown-leave-active) .header-journeys-dropdown__image-section {
  opacity: 0;
  transition: opacity 0.05s ease;
}

@keyframes journeyImageFadeIn {
  to {
    opacity: 1;
  }
}
</style>
